import { supabase } from "@/integrations/supabase/client"
import { Database } from "@/types/supabase"
import { logger } from "@/lib/services/logger-service"

// Simplified types with only essential fields
export interface Child {
  id: string;
  name: string;
  date_of_birth: string;
  caregiver_id?: string; // Kept for relationship queries if needed
  isdeleted?: boolean;
  archived?: boolean;
}

export interface ChildInsert {
  name: string;
  date_of_birth: string;
  caregiver_id: string;
  isdeleted?: boolean;
}

export interface ChildUpdate {
  name?: string;
  date_of_birth?: string;
  isdeleted?: boolean;
  archived?: boolean;
  updated_at?: string;
}

export class ChildRepository {
  static async getByCaregiver(caregiverId: string): Promise<Child[]> {
    logger.debug('ChildRepository.getByCaregiver called', {
      component: 'ChildRepository',
      method: 'getByCaregiver',
      caregiverId,
      caregiverIdType: typeof caregiverId
    });

    const { data, error } = await supabase
      .from('children')
      .select('id, name, date_of_birth')
      .eq('caregiver_id', caregiverId)
      .eq('isdeleted', false) // Only fetch non-deleted children
      .or('archived.is.null,archived.eq.false') // Exclude archived children
      .order('name')

    if (error) {
      logger.error('Error fetching children in repository', {
        component: 'ChildRepository',
        method: 'getByCaregiver',
        error,
        caregiverId,
        stackTrace: new Error().stack
      });
      console.error('Error fetching children:', error)
      throw error
    }

    logger.debug('Children fetched successfully', {
      component: 'ChildRepository',
      method: 'getByCaregiver',
      caregiverId,
      count: data?.length || 0,
      children: data?.map(c => ({ id: c.id, name: c.name, idType: typeof c.id }))
    });

    return data || []
  }

  static async create(child: ChildInsert): Promise<Child> {
    const { data, error } = await supabase
      .from('children')
      .insert([child])
      .select()
      .single()

    if (error) {
      console.error('Error creating child:', error)
      throw error
    }

    return data
  }

  static async update(childId: string, updates: ChildUpdate): Promise<Child> {
    logger.debug('ChildRepository.update called', {
      component: 'ChildRepository',
      method: 'update',
      childId,
      childIdType: typeof childId,
      updates
    });

    const { data, error } = await supabase
      .from('children')
      .update(updates)
      .eq('id', childId)
      .select()
      .single()

    if (error) {
      logger.error('Error updating child in repository', {
        component: 'ChildRepository',
        method: 'update',
        error,
        childId,
        updates,
        stackTrace: new Error().stack
      });
      console.error('Error updating child:', error)
      throw error
    }

    logger.debug('Child updated successfully', {
      component: 'ChildRepository',
      method: 'update',
      childId,
      updatedChild: data
    });

    return data
  }

  static async getDeletedChildByNameAndCaregiver(name: string, caregiverId: string): Promise<Child | null> {
    const { data, error } = await supabase
      .from('children')
      .select('id, name, date_of_birth, isdeleted')
      .eq('caregiver_id', caregiverId)
      .eq('name', name)
      .eq('isdeleted', true)
      .maybeSingle() // Returns null if not found instead of throwing an error

    if (error) {
      console.error('Error checking for deleted child:', error)
      throw error
    }

    return data
  }

  static async reactivateChild(childId: string): Promise<Child> {
    const { data, error } = await supabase
      .from('children')
      .update({ 
        isdeleted: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', childId)
      .select()
      .single()

    if (error) {
      console.error('Error reactivating child:', error)
      throw error
    }

    return data
  }
}
